import { useEffect, useRef } from "react";
import { DataIngestionResponse } from "datasets/pages/tables/types/interfaces/ingestion-job";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Stepper,
  StepperIndicator,
  StepperItems,
  useStepperContext,
} from "~/components/ui/stepper";
import { ValidationNavigation } from "./ValidationNavigation";
import { FileUploadStep, FileValidationStep, SummaryStep } from "./ValidationSteps";
import { IngestionProvider, useIngestionContext } from "./context/IngestionContext";
import { StartProfilingParams, ProfilingResult } from "./hooks/useStartProfiling";
import { useValidationAndProfiling } from "./hooks/useValidationAndProfiling";
import { FileValidationModalProps, DataProfile, ValidationState, ProfilingError } from "./types";

interface StartValidationParams {
  sourceFile: string;
}

interface ValidationResult {
  ingestionId: string;
  validationJobId: string;
  profilingJobId?: string;
}

interface StepperStateSyncProps {
  validationState: ValidationState;
  dataProfile: DataProfile | null;
  profilingError: ProfilingError | null;
  isValidating: boolean;
  isProfiling: boolean;
  startValidation: (params: StartValidationParams) => Promise<ValidationResult | null>;
  startProfiling: (params: StartProfilingParams) => Promise<ProfilingResult | null>;
  ingestion: DataIngestionResponse | undefined;
  isLoadingIngestion: boolean;
  /** Source file URI recovered from existing jobs (for page refresh scenario) */
  recoveredSourceFile: string | null;
}
/**
 * Component to sync validation/profiling state to stepper context.
 * This allows child step components to access validation state.
 *
 * File selection is handled by IngestionContext directly.
 * StepperStateSync only handles validation/profiling state.
 */
const StepperStateSync = ({
  validationState,
  dataProfile,
  profilingError,
  isValidating,
  isProfiling,
  startValidation,
  startProfiling,
  ingestion,
  isLoadingIngestion,
  recoveredSourceFile,
}: StepperStateSyncProps) => {
  const { setStepperState } = useStepperContext();
  const hasInitialized = useRef(false);

  // Sync state to stepper context, using ref to prevent render loops
  useEffect(() => {
    if (!hasInitialized.current && !ingestion && !isValidating && !isProfiling) {
      return;
    }
    hasInitialized.current = true;

    setStepperState?.((prev: any) => ({
      ...prev,
      validationState,
      dataProfile,
      profilingError,
      isValidating,
      isProfiling,
      startValidation,
      startProfiling,
      ingestion,
      isLoadingIngestion,
      recoveredSourceFile,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    validationState,
    dataProfile,
    profilingError,
    isValidating,
    isProfiling,
    ingestion,
    isLoadingIngestion,
    recoveredSourceFile,
  ]);

  return null;
};

/**
 * Inner modal content that has access to IngestionContext.
 */
const FileValidationModalContent = ({
  onClose,
  onFileValidated,
}: Omit<FileValidationModalProps, "open">) => {
  const { selectedFile, pendingLocalFile, reset } = useIngestionContext();

  // Don't pass sourceFile if there's a pending upload - wait for upload to complete
  // This prevents validation from starting with the wrong (old) file
  const sourceFileUri = pendingLocalFile ? null : selectedFile?.s3_uri || selectedFile?.key;
  const {
    validationState,
    dataProfile,
    profilingError,
    isValidating,
    isProfiling,
    startValidation,
    startProfiling,
    ingestion,
    isLoadingIngestion,
    recoveredSourceFile,
  } = useValidationAndProfiling({ sourceFile: sourceFileUri });

  const stepLabels = ["File Upload", "File Validation", "Summary"];

  // Initial stepper state - validation/profiling only
  // File selection is handled by IngestionContext
  const stepperInitialValue = {
    validationState,
    dataProfile,
    profilingError,
    isValidating,
    isProfiling,
    startValidation,
    startProfiling,
    ingestion,
    isLoadingIngestion,
    recoveredSourceFile,
  };

  /**
   * Handle modal close with cleanup
   */
  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <DialogContent
      className="sm:max-w-3xl max-h-[95vh] flex flex-col"
      onEscapeKeyDown={(e) => e.preventDefault()}
      onPointerDownOutside={(e) => e.preventDefault()}
    >
      <DialogHeader className="shrink-0">
        <DialogTitle>File Validation</DialogTitle>
        <DialogDescription>
          Upload and validate your file before proceeding with ingestion.
        </DialogDescription>
      </DialogHeader>

      <Stepper initialValue={stepperInitialValue} initialStep={0}>
        {/* Sync validation state to stepper context for child components */}
        <StepperStateSync
          validationState={validationState}
          dataProfile={dataProfile}
          profilingError={profilingError}
          isValidating={isValidating}
          isProfiling={isProfiling}
          startValidation={startValidation}
          startProfiling={startProfiling}
          ingestion={ingestion}
          isLoadingIngestion={isLoadingIngestion}
          recoveredSourceFile={recoveredSourceFile}
        />

        <div className="flex flex-col flex-1 min-h-0">
          <div className="shrink-0 mb-4">
            <StepperIndicator stepLabels={stepLabels} />
          </div>

          <div className="flex-1 overflow-y-auto">
            <StepperItems>
              <FileUploadStep />
              <FileValidationStep />
              <SummaryStep />
            </StepperItems>
          </div>
        </div>

        <ValidationNavigation onClose={handleClose} onFileValidated={onFileValidated} />
      </Stepper>
    </DialogContent>
  );
};

/**
 * FileValidationModal
 *
 * Modal for validating files before ingestion.
 * Wrapped with IngestionProvider to manage ingestion lifecycle.
 */
const FileValidationModal = ({ open, onClose, onFileValidated }: FileValidationModalProps) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <IngestionProvider>
        <FileValidationModalContent onClose={onClose} onFileValidated={onFileValidated} />
      </IngestionProvider>
    </Dialog>
  );
};

export default FileValidationModal;
